'use client';

/**
 * WebQARendererSlotCard — Klyde Reception card-rendered answer slot.
 *
 * The dispatched-card-slot pattern. Reads
 * `useDocsStore.response.renderer_card`. When the agent's response
 * carries a content_key (e.g. *"Show me the Monte Carlo for 2026"*
 * → `cost_forecast_burn_canvas`), this slot mounts that card from
 * CANVAS_CARD_REGISTRY. The mounted card fetches its own content
 * via its strategy_hint, identical to any other canvas mount.
 *
 * Empty state — subtle placeholder. Doesn't demand attention; just
 * teaches the user that data answers are possible. The pure
 * substrate flow: agent recommendation → registry lookup → mount.
 *
 * Reusable beyond Reception: any agent-recommendation surface
 * (capability_search results, related-cards, quick-action panels)
 * can render with the same primitive — give it a content_key, it
 * mounts. The lookup table is the same one used by canvas_2 and
 * the rest of the canvas pipeline; nothing bespoke.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { type ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useDocsStore } from '@/lib/stores/docs-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { resolveCard } from '@/components/cards/unified-card-registry';
import { CardErrorBoundary } from '../card-error-boundary';
import { QA_SECTION } from './web-qa-chrome';


export function WebQARendererSlotCard() {
  const T = useCardTypography();
  const renderer = useDocsStore((s) => s.response?.renderer_card ?? null);

  // The mount site is where a project-scoped card gets its entity — without
  // this it mounts with no entityId and shows "Select a project". Two
  // perspectives, in precedence: a selected DETAIL (rfq/product/actor), else
  // the selected PROJECT, else the caller's recent project (the ambient "my
  // project" anchor, resolved server-side). Hooks run unconditionally, before
  // the early returns, per React rules. The fetch is shared/cached across cards.
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const projectId = useGraphSelectionStore((s) => s.projectId);
  // The compositor publishes the resolved entity alongside the recommended card
  // (ki_ask resolved it from the caller); an entity-scoped pick binds to THAT,
  // not the ambient project. Null for caller-scoped cards (home_pipeline, etc.).
  const recommendedEntitySid = useDocsStore(
    (s) => (s.response as { renderer_entity_sid?: string | null } | null)?.renderer_entity_sid ?? null,
  );
  // Per-card render height (BFF-shipped CardSize). A placement-less slot can't
  // guess height: a compact answer must size to content, a Recharts card needs
  // a definite box or it collapses. Default 'compact' when the BFF didn't ship one.
  const recommendedSize = useDocsStore(
    (s) => (s.response as { renderer_size?: string | null } | null)?.renderer_size ?? 'compact',
  );
  const { data: recentProject } = useQuery({
    queryKey: ['caller_recent_project'],
    queryFn: async () => {
      const res = await api.getStrategyData<{ project_sid?: string | null }>(
        'caller_recent_project',
        {},
      );
      return res.success ? res.data : null;
    },
    staleTime: 5 * 60 * 1000,
  });
  const entityId =
    recommendedEntitySid ?? selectedNode?.id ?? projectId ?? recentProject?.project_sid ?? undefined;

  // Substrate-presence-driven — nothing until the agent's response names
  // a renderer_card. Mirrors the doctrine the other QA slice cards follow.
  if (!renderer) return null;

  // Cast to accept the optional entity prop — registry components are typed
  // bare (React.ComponentType); project-scoped cards (rfi_threads, etc.) read
  // `entityId`, entity-free cards ignore the extra prop harmlessly.
  const Mount = resolveCard(renderer) as ComponentType<{ entityId?: string }> | undefined;

  // Defensive — agent recommended a content_key the registry doesn't know.
  // Surface the gap visibly so it gets fixed (registry sync, missing card)
  // rather than silently hiding the answer.
  if (!Mount) {
    return (
      <section className={QA_SECTION}>
        <div className="w-full">
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-6 py-8 text-center">
            <p className={cn(T.metadata, 'text-destructive')}>
              Renderer for <span className="font-mono">{renderer}</span> not in registry.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Reserve height per-card from the BFF-shipped CardSize. 'compact' sizes to
  // content (a pipeline summary stays compact, capped with a scroll); 'tall'
  // and 'chart' reserve a definite height so a fill-container Recharts card
  // (cost_forecast_burn) has a parent height to fill instead of collapsing.
  const heightClass =
    recommendedSize === 'chart' ? 'h-[680px]'
      : recommendedSize === 'tall' ? 'h-[480px]'
        : 'max-h-[80vh]';
  return (
    <section className={QA_SECTION}>
      <div className={cn('w-full overflow-y-auto', heightClass)}>
        <CardErrorBoundary contentKey={renderer}>
          <Mount entityId={entityId} />
        </CardErrorBoundary>
      </div>
    </section>
  );
}
